import type { OtpBlock } from '@blogforge/shared';
import type { DragPath } from 'src/types';

// ─────────────────────────────────────────────────────────────────
// Canvas preview for the OTP block — a verification-code display,
// e.g. "481 520" in evenly-spaced digit boxes.
//
// Three things to know:
//
// 1. Blocks are never zod-parsed on the way in (same story as
//    StatRenderer): BlockAdder builds raw objects and EditorPage
//    JSON.parses, so the schema's `.default()`s (length: 6, boxed:
//    true, …) are absent at runtime. Every attr is treated as optional.
//
// 2. `code` is almost always the send-time token `{{otp}}`, which only
//    resolves in emailOtp(). The canvas has no token context, so
//    anything that looks like a token — or is empty — previews with
//    mock digits. A literal value previews as-is.
//
// 3. Sizing mirrors emailOtp() exactly (26px boxes for >6 digits, 34px
//    otherwise, font 18/22px) so the canvas doesn't lie about mobile
//    fit. If you change one, change the other.
// ─────────────────────────────────────────────────────────────────

const TOKEN_RE = /^\{\{.*\}\}$/;
const MOCK = '4815209371'; // 10 chars — enough for length up to 8, after any slice

export function OtpRenderer({
  block,
}: {
  block: OtpBlock;
  path: DragPath;
  rawDefinitions: unknown;
}) {
  const length = block.attrs?.length ?? 6;
  const groupSize = block.attrs?.groupSize ?? 3;
  const boxed = block.attrs?.boxed ?? true;
  const label = block.attrs?.label ?? '';
  const rawCode = block.attrs?.code ?? '';

  const isToken = rawCode === '' || TOKEN_RE.test(rawCode);
  const resolved = (isToken ? MOCK : rawCode).replace(/\s+/g, '').slice(0, length);

  // Fallback sizes mirror emailOtp(): chosen so a full-width code fits a
  // 320px viewport even if the email shell's <style> block is stripped.
  const wide = length > 6;
  const boxW = wide ? 26 : 34;
  const boxH = wide ? 34 : 44;
  const fontPx = wide ? 18 : 22;

  return (
    <div>
      {block.content && <h3 className="doc-text mb-3 text-lg font-semibold">{block.content}</h3>}

      <div className="border-line bg-bg-soft rounded-lg border border-dashed px-4 py-6 text-center">
        {label && <div className="text-muted mb-3 text-xs">{label}</div>}

        <div className="flex flex-nowrap items-center justify-center">
          {resolved.split('').map((d, i) => {
            const isLast = i === resolved.length - 1;
            const gapAfter = groupSize > 0 && (i + 1) % groupSize === 0 && !isLast;

            return (
              <span
                key={i}
                className={[
                  'doc-text font-mono font-bold',
                  boxed ? 'bg-bg inline-flex items-center justify-center rounded-md' : '',
                  isLast ? 'mr-0' : gapAfter ? 'mr-2.5' : 'mr-1.5',
                ].join(' ')}
                style={
                  boxed
                    ? { width: boxW, height: boxH, fontSize: fontPx, lineHeight: 1 }
                    : { fontSize: fontPx + 4, lineHeight: 1 }
                }
              >
                {d}
              </span>
            );
          })}
        </div>

        {isToken && (
          <p className="text-muted mt-3 text-[11px]">
            Preview digits — the real code replaces <code className="font-mono">{'{{otp}}'}</code>{' '}
            at send time
          </p>
        )}
      </div>
    </div>
  );
}
